import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await register(name, email, password);
      toast.success("Welcome. Let's build capacity.");
      navigate("/dashboard");
    } catch (e2) {
      const msg = formatApiErrorDetail(e2.response?.data?.detail) || "Please try again.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-navy text-white">
      <div className="relative hidden lg:block">
        <div className="absolute inset-0 grain-overlay">
          <div className="absolute -top-24 -left-24 h-[500px] w-[500px] rounded-full bg-brand-teal/25 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 h-[500px] w-[500px] rounded-full bg-brand-magenta/25 blur-3xl" />
        </div>
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" data-testid="register-back-home"><Logo variant="light" /></Link>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-magenta">Start your journey</p>
            <h1 className="mt-4 font-display font-black text-4xl xl:text-5xl leading-tight tracking-tight">
              Between appointments is where change happens.
            </h1>
            <p className="mt-6 max-w-md text-white/75 text-lg leading-relaxed">
              Create your participant space in 30 seconds. Track goals, log wins, and keep your whole team pointed the same way.
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-12 py-10 bg-brand-sand text-brand-navy">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-mute hover:text-brand-navy text-sm mb-8" data-testid="register-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <h2 className="font-display font-bold text-3xl tracking-tight">Create your account</h2>
          <p className="mt-2 text-brand-mute">Free. Private. Yours.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="reg-name">Your name</Label>
              <Input
                id="reg-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
                className="mt-2 h-11"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="reg-email">Email</Label>
              <Input
                id="reg-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-2 h-11"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="reg-password">Password</Label>
              <Input
                id="reg-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                className="mt-2 h-11"
                data-testid="register-password-input"
              />
              <p className="mt-1 text-xs text-brand-mute">At least 6 characters.</p>
            </div>
            {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}
            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-full h-12 bg-brand-magenta hover:bg-brand-magentaDark text-white btn-lift"
              data-testid="register-submit-button"
            >
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-brand-mute">
            Already have an account?{" "}
            <Link to="/login" className="text-brand-teal font-semibold hover:underline" data-testid="register-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
